import styled from "styled-components";

export const Card = styled.article`
  background: var(--white);
  border-radius: var(--border-radius);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 4px solid var(--status-color, var(--border));
  box-shadow: var(--shadow-sm);
  margin-bottom: 0.5rem;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  overflow: hidden;

  &:hover {
    box-shadow: var(--shadow);
    transform: translateY(-1px);
  }
`;

export const CardHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  flex-wrap: wrap;
`;

export const QuoteId = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-label);
  white-space: nowrap;
  font-family: monospace;
  letter-spacing: 0.03em;
`;

export const PatientName = styled.h2`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
`;

export const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
`;

export const CardBody = styled.section`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 1rem;
  padding: 0.5rem 1rem 0.625rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  @media (min-width: 576px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(5, auto);
    justify-content: start;
    gap: 0 2.5rem;
  }
`;

export const MetaItem = styled.div`
  padding: 0.375rem 0;
`;

export const MetaLabel = styled.p`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-label);
  margin: 0 0 0.125rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const MetaValue = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--text-primary);
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MetaValueMuted = styled(MetaValue)`
  color: var(--text-label);
  font-weight: 400;
  font-style: italic;
`;

export const MetaValueDanger = styled(MetaValue)`
  color: var(--danger);
  display: flex;
  align-items: center;
  gap: 0.2rem;
`;

export const MetaValuePrimary = styled(MetaValue)`
  color: var(--primary);
`;

export const MetaSub = styled.small`
  font-size: 0.6875rem;
  color: var(--text-label);
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
`;

export const CardFooter = styled.footer`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  flex-wrap: wrap;
`;

export const PaymentBadge = styled.span<{ $dark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.1875rem 0.625rem;
  border-radius: 2rem;
  background: ${({ $dark }) =>
    $dark ? "rgba(0,0,0,0.07)" : "rgba(var(--primary-rgb), 0.1)"};
  color: ${({ $dark }) => ($dark ? "var(--text-secondary)" : "var(--primary)")};
  border: 1px solid
    ${({ $dark }) =>
      $dark ? "rgba(0,0,0,0.1)" : "rgba(var(--primary-rgb), 0.2)"};
  letter-spacing: 0.02em;
  text-transform: uppercase;

  i {
    font-size: 0.875rem;
  }
`;

export const DocIndicator = styled.span<{
  $active?: boolean;
  $partial?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.1875rem 0.5rem;
  border-radius: 2rem;
  transition: opacity 0.15s ease;

  /* complete */
  ${({ $active }) =>
    $active &&
    `
    opacity: 1;
    color: #15803d;
    background: rgba(21,128,61,0.08);
    border: 1px solid rgba(21,128,61,0.2);
  `}

  /* partial — âmbar */
  ${({ $partial, $active }) =>
    $partial &&
    !$active &&
    `
    opacity: 1;
    color: #b45309;
    background: rgba(217,119,6,0.08);
    border: 1px solid rgba(217,119,6,0.2);
  `}

  /* none */
  ${({ $active, $partial }) =>
    !$active &&
    !$partial &&
    `
    opacity: 0.4;
    color: var(--gray);
    background: transparent;
    border: 1px solid rgba(0,0,0,0.1);
  `}

  i {
    font-size: 0.875rem;
  }
`;

/* ── Documento Badge — visível no header do card ── */
type DocState = "none" | "partial" | "complete";

export const DocsBadge = styled.span<{ $state: DocState }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.25rem 0.625rem;
  border-radius: 2rem;
  white-space: nowrap;
  flex-shrink: 0;
  letter-spacing: 0.02em;
  transition: var(--transition);

  /* complete — verde */
  ${({ $state }) =>
    $state === "complete" &&
    `
    background: rgba(21, 128, 61, 0.1);
    color: #15803d;
    border: 1px solid rgba(21, 128, 61, 0.25);
  `}

  /* partial — âmbar */
  ${({ $state }) =>
    $state === "partial" &&
    `
    background: rgba(217, 119, 6, 0.1);
    color: #b45309;
    border: 1px solid rgba(217, 119, 6, 0.25);
  `}

  i {
    font-size: 0.9375rem;
  }
`;

export const DetailsButton = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 0.4rem 1rem;
  border-radius: 2rem;
  background: var(--primary);
  color: #fff;
  text-decoration: none;
  transition: var(--transition);
  white-space: nowrap;
  cursor: pointer;
  letter-spacing: 0.02em;

  &:hover {
    background: var(--primary-hover);
    color: #fff;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  i {
    font-size: 1rem;
  }

  &.has-pending {
    background: var(--danger);

    &:hover {
      filter: brightness(1.1);
    }
  }
`;
